<template>
  <v-autocomplete
    class="nx-form-select"
    :attach="calculateAttach()"
    v-validate="validation"
    data-vv-validate-on="input|blur"
    data-vv-value-path="value"
    v-bind="$attrs"
    :data-vv-scope="scope"
    :data-vv-name="name"
    :data-vv-as="label"
    :ref="name"
    :name="name"
    :label="label"
    :multiple="multiple"
    :items="translatedItems"
    :required="required"
    :disabled="disabled"
    :error-messages="errorMessages"
    :chips="multiple"
    :value="value"
    :placeholder="placeholderText"
    :hint="hintComputedValue"
    @input="onInput"
    @blur="onBlur"
    @focus="onFocus"
    :item-avatar="itemsHasImage"
    v-on="$listeners"
  >
    <template v-slot:selection="data" v-if="itemsHasImage">
      <v-chip
        :selected="data.selected"
        :close="multiple"
        class="chip--select-multi"
      >
        <v-avatar>
          <img :src="data.item.image">
        </v-avatar>
        {{ data.item.text }}
      </v-chip>
    </template>
    <template v-if="canSelectAll" v-slot:prepend-item>
      <v-list-tile ripple @click="selectAllItems">
        {{ $t(`message.block.${selectAllText}`) }}
      </v-list-tile>
      <v-divider v-if="canSelectAll" class="mt-2"></v-divider>
    </template>
    <template v-else v-slot:item="data" class="py-3">
      <v-list-tile-action class="mr-3" v-if="itemsHasImage">
        <v-img :height="80"
               :width="142"
               :src="data.item.image"
               :lazy-src="data.item.image"
               class="grey lighten-2"
               contain
        ></v-img>
      </v-list-tile-action>
      <v-list-tile-content>
        <v-list-tile-title v-html="data.item.text"></v-list-tile-title>
      </v-list-tile-content>
    </template>
  </v-autocomplete>
</template>

<script>
import FormElement from '@/modules/form/mixins/form-element';
import isObject from 'lodash/isObject';
import { elementHasFixedOrStickyPosition, translateCandidates } from '@/utils';
import HasCustomHintEndpoint from '@/modules/form/mixins/has-custom-hint-endpoint';

export default {
  name: 'nx-form-select',
  mixins: [
    FormElement,
    HasCustomHintEndpoint,
  ],
  data() {
    return {
      currentValue: this.value,
    };
  },
  props: {
    value: {
      type: [Array, String, Number, Boolean, Object],
    },
    items: {
      type: Array,
      required: true,
    },
    limit: {
      type: Number,
      required: false,
      default: 1,
    },
    canSelectAll: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
    },
  },
  methods: {
    onBlur() {
      this.$emit('blur', this.currentValue);
      this.flags.focused = false;
    },
    onInput($event) {
      this.currentValue = $event;
      this.$emit('input', this.currentValue);
      this.$parentForm.updateField(this.name, this.currentValue);
    },
    selectAllItems() {
      this.currentValue = this.currentValue.length > 0 ? [] : this.items.map(item => item.value);
      this.$emit('input', this.currentValue);
      this.$parentForm.updateField(this.name, this.currentValue);
    },
    /**
     * Default value handler.
     * Implemented as a method so it can be overrided.
     */
    onDefault(defaultValue) {
      this.$emit('input', defaultValue);
      this.$parentForm.updateField(this.name, defaultValue, { defaultUpdate: true });
      this.$emit('blur', defaultValue);
      this.$nextTick(this.validate);
    },
    calculateAttach() {
      return elementHasFixedOrStickyPosition(this.$el);
    },
  },
  computed: {
    multiple() {
      return this.limit !== 1;
    },
    itemsHasImage() {
      return this.items.some(item => item.image) ? [] : undefined;
    },
    createItemsWithGroups() {
      const currentItems = [];
      let currentGroup = null;
      if (this.items.find(o => o.group)) {
        this.items.forEach((item, index) => {
          if (item.group && item.group !== currentGroup) {
            if (currentGroup) currentItems.push({ divider: true });
            currentItems.push({
              header: translateCandidates([
                `message.form.formSelect.${this.name}.groups.${item.group}`,
                `message.${item.group}`,
                item.group,
              ], item.group),
            });
            currentGroup = item.group;
          }
          currentItems.push(item);
        });
        return currentItems;
      }
      return this.items;
    },
    translatedItems() {
      return this.createItemsWithGroups
        .map((item) => {
          if (isObject(item)) return item;
          return {
            text: item,
            value: item,
          };
        })
        .map((item) => {
          if (item.divider) {
            return ({ divider: item.divider });
          }
          if (item.header) {
            return ({ header: item.header });
          }
          return ({
            value: item.value,
            image: item.image,
            text: translateCandidates([
              `message.form.formSelect.${this.scope}.${this.name}.${item.text}`,
              `message.form.formSelect.${item.text}`,
              `message.${item.text}`,
              item.text,
            ], item.text),
          });
        });
    },
    selectAllText() {
      if (Array.isArray(this.currentValue) && this.currentValue.length > 0) {
        return 'deleteAll';
      }
      return 'selectAll';
    },
    placeholderText() {
      if (!this.placeholder) {
        return null;
      }
      return this.$te(this.placeholder) ? this.$t(this.placeholder) : this.placeholder;
    },
  },
};
</script>
<style lang="stylus">
.nx-form-select .v-list__tile--avatar {
  height: auto;
  margin: 0 0 10px;
}
</style>
